import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EmptyNote } from '../../components/RecordScreen';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { formatDate } from '../../lib/format';
import { ScheduleEditorModal } from './ScheduleEditorModal';
import {
  DAY_SHORT,
  describeDayTimes,
  inWeekOrder,
  NO_WORKING_WEEK_COSTS,
  SCHEDULES_PATH,
  type StaffSchedule,
  scheduleComingUp,
  scheduleInForce,
  summarize,
} from './schedule';

/**
 * The pattern someone is expected to work, and the history of what it used to be.
 *
 * Without this the hours a manager approves have nothing to be measured against — a seven-hour day
 * and a short day look identical. Schedules are effective-dated for the same reason pay is: last
 * month's timesheet has to be readable against the schedule that was in force then, not the one
 * agreed yesterday.
 *
 * The same week is also readable and settable for the whole team beside the hours it decides, on
 * Time & pay. This one stays because it is a fact about a person and this is the person's page;
 * both open the one editor, so there is no second way to write a schedule.
 */
export function SchedulePanel({ staffId, staffName }: { staffId: string; staffName: string }) {
  const [editing, setEditing] = useState(false);

  const schedules = useQuery({
    queryKey: listKey(SCHEDULES_PATH, { staffId }),
    queryFn: () => rawApi<StaffSchedule[]>('GET', `${SCHEDULES_PATH}?staffId=${staffId}`),
    enabled: Boolean(staffId),
  });

  const history = schedules.data ?? [];
  // The BAR's calendar, not the reader's browser: `useIslandToday` carries the reasoning and the
  // six times a UTC-sliced date has answered tomorrow from six in the evening.
  const today = useIslandToday();
  const current = scheduleInForce(history, today);
  const upcoming = scheduleComingUp(history, today);

  return (
    <>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ minWidth: 0 }}>
          {current ? (
            <Stack gap={4}>
              <Text size="sm" fw={600}>
                {summarize(current.pattern)}
              </Text>
              <Group gap="xs" wrap="wrap">
                {inWeekOrder(current.pattern).map((d) => (
                  <Text key={d.weekday} size="xs" c="dimmed">
                    {DAY_SHORT[d.weekday]} {describeDayTimes(d)}
                  </Text>
                ))}
              </Group>
              <Text size="xs" c="dimmed">
                In force since {formatDate(current.effectiveFrom)}
              </Text>
            </Stack>
          ) : (
            /* Said as what it costs, not as an empty field. A person with no working week is the
               single most expensive gap in payroll here, and it was being reported as a shrug. */
            <EmptyNote>No working week set. {NO_WORKING_WEEK_COSTS}</EmptyNote>
          )}
        </div>
        <Button size="sm" variant="light" onClick={() => setEditing(true)}>
          {current ? 'Change schedule' : 'Set schedule'}
        </Button>
      </Group>

      {upcoming && (
        <Alert color="blue" variant="light" mt="md" title="A new working week starts soon">
          {summarize(upcoming.pattern)} from {formatDate(upcoming.effectiveFrom)}.
        </Alert>
      )}

      {editing && (
        <ScheduleEditorModal
          staffId={staffId}
          staffName={staffName}
          currentPattern={current?.pattern ?? []}
          onClose={() => setEditing(false)}
        />
      )}
    </>
  );
}
